import {
  genericForwardRefMemo,
  genericMemo,
} from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up_filled.svg";
import classNames from "classnames";
import {
  CSSProperties,
  ForwardedRef,
  MouseEvent,
  ReactNode,
  useState,
} from "react";
import { Flex } from "../../flex";
import { Icon } from "../../icon";
import { TextSizeValue } from "../../theme/typography";
import { DescriptionText, LabelText } from "../common/descriptors";
import {
  ListItemRender,
  RedoListItem,
  RedoListItemSelectedSource,
} from "../list/redo-list";
import {
  RedoListDropdown,
  dropdownSizeToListSize,
} from "../list/redo-list-dropdown";
import { TextRedoListItem } from "../list/standard-redo-list-items";
import { RedoSpinner } from "../loading/redo-spinner";
import * as redoDropdownInputCss from "./redo-dropdown-input.module.css";

export const redoDropdownInputSize = ["xs", "sm", "md"] as const;
export type RedoDropdownInputSize = (typeof redoDropdownInputSize)[number];

export const redoDropdownInputState = ["default", "error", "readonly"] as const;
export type RedoDropdownInputState = (typeof redoDropdownInputState)[number];

export const RedoSingleSelectDropdownInput = genericMemo(
  function RedoSingleSelectDropdownInput<T>({
    autoFocus,
    options,
    optionSelected,
    disabledItems,
    disabledTooltips,
    selectedItem,
    emptyListMessage,
    disabled = false,
    readonly = false,
    placeholder,
    className,
    size = "xs",
    fitToAnchor = true,
    label,
    description,
    onBlur,
    flexGrow = true,
    fullWidth = false,
    overflow,
    searchString,
    setSearchString,
    searchPlaceholder,
    itemsLoading,
    dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
    hideBorder,
    dataTestId,
    state = "default",
  }: {
    autoFocus?: boolean;
    options: RedoListItem<T>[];
    optionSelected(args: {
      item: RedoListItem<T>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }): void;
    disabledItems?: (string | number)[] | (string | number);
    disabledTooltips?: Record<string | number, string>;
    selectedItem?: RedoListItem<T>;
    size?: RedoDropdownInputSize;
    emptyListMessage?: string;
    itemsLoading?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    placeholder?: string;
    className?: string;
    fitToAnchor?: boolean;
    label?: string | ReactNode;
    onBlur?: () => void;
    description?: string;
    flexGrow?: boolean;
    fullWidth?: boolean;
    overflow?: "hidden" | undefined;
    searchString?: string;
    setSearchString?: (search: string) => void;
    searchPlaceholder?: string;
    dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
    hideBorder?: boolean;
    dataTestId?: string;
    state?: RedoDropdownInputState;
  }) {
    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(autoFocus || false);

    const handleDropdownStateChange = useHandler((open: boolean) => {
      setSearchString?.("");
      setDropdownOpen(open);
      if (!open) {
        onBlur?.();
      }
    });

    const descriptorFontSize = sizeToDescriptorTextProps[size];

    const onItemSelected = useHandler(
      (args: {
        item: RedoListItem<T>;
        source: RedoListItemSelectedSource;
        index: number;
        selected: boolean;
      }) => {
        handleDropdownStateChange(false);
        optionSelected(args);
      },
    );

    return (
      <>
        <Flex
          dir="column"
          gap="sm"
          grow={flexGrow ? 1 : undefined}
          overflow={overflow}
          w={fullWidth ? "full" : undefined}
        >
          {typeof label === "string" ? (
            <LabelText label={label} size={descriptorFontSize} />
          ) : (
            label
          )}
          <RedoSingleSelectDropdownInputButton
            className={className}
            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
            }
            dataTestId={dataTestId}
            disabled={disabled}
            hideBorder={hideBorder}
            isDropdownOpen={dropdownOpen}
            loading={itemsLoading}
            onClick={(e) => {
              handleDropdownStateChange(!dropdownOpen);
            }}
            placeholder={placeholder}
            readonly={readonly}
            ref={setDropdownButtonRef}
            selectedItem={selectedItem}
            size={size}
            state={state}
          />
          <DescriptionText
            description={description}
            error={state === "error"}
            size={descriptorFontSize}
          />
        </Flex>
        <RedoListDropdown
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
          }
          disabledItems={disabledItems}
          disabledTooltips={disabledTooltips}
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          emptyListMessage={emptyListMessage}
          fitToAnchor={fitToAnchor}
          items={options}
          itemSelected={onItemSelected}
          itemsLoading={itemsLoading}
          key={`${dropdownOpen}`}
          searchPlaceholder={searchPlaceholder}
          searchString={searchString}
          selectedItems={selectedItem?.id}
          setDropdownOpen={handleDropdownStateChange}
          setSearchString={setSearchString}
          size={size}
        />
      </>
    );
  },
);

interface RedoSingleSelectDropdownInputButtonProps<T> {
  size?: RedoDropdownInputSize;
  isDropdownOpen: boolean;
  selectedItem?: RedoListItem<T>;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  ref?: any;
  hideBorder?: boolean;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  dataTestId?: string;
  loading?: boolean;
  state?: RedoDropdownInputState;
}

export const RedoSingleSelectDropdownInputButton = genericForwardRefMemo(
  function RedoSingleSelectDropdownInputButton<T>(
    {
      selectedItem,
      isDropdownOpen,
      size = "sm",
      disabled = false,
      readonly = false,
      placeholder,
      className,
      loading = false,
      hideBorder = false,
      onClick,
      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      dataTestId,
      state = "default",
    }: RedoSingleSelectDropdownInputButtonProps<T>,
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    const buttonOverrideStyles: CSSProperties = {
      ...dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      ...(dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?.outlineColor
        ? {
            borderColor:
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.outlineColor,
            borderWidth: "1px",
            borderStyle: "solid",
          }
        : {}),
      ...(hideBorder && { borderWidth: 0, paddingTop: 0, paddingBottom: 0 }),
    };

    const content = (
      <Flex align="center" dir="row" gap="sm" overflow="hidden">
        {selectedItem ? (
          <ListItemRender
            card={false}
            customStyles={dangerousStyleThatShouldOnlyBeUsedForMerchantBranding}
            disabled={disabled}
            focused={false}
            idx={0}
            item={selectedItem}
            selected={false}
            selectionVariant="none"
            size={dropdownSizeToListSize[size]}
            useWrapper={false}
          />
        ) : (
          <TextRedoListItem
            customStyles={dangerousStyleThatShouldOnlyBeUsedForMerchantBranding}
            placeholder
            size={dropdownSizeToListSize[size]}
            text={placeholder || ""}
            useWrapper={false}
          />
        )}
      </Flex>
    );

    if (readonly) {
      return content;
    }

    return (
      <button
        className={classNames(
          className,
          hideBorder && redoDropdownInputCss.hideBorder,
          redoDropdownInputCss.singleSelectInput,
          sizeClassToCssClass[size],
          isDropdownOpen && redoDropdownInputCss.open,
          disabled && redoDropdownInputCss.disabled,
          state === "error" && redoDropdownInputCss.error,
        )}
        data-testid={dataTestId}
        disabled={disabled || readonly}
        onClick={onClick}
        ref={ref}
        style={buttonOverrideStyles}
        type="button"
      >
        {content}
        {loading ? (
          <RedoSpinner size="xs" />
        ) : (
          <Icon
            arbiterIconSvg={isDropdownOpen ? ChevronUpIcon : ChevronDownIcon}
            className={redoDropdownInputCss.chevron}
            color="ghost"
          />
        )}
      </button>
    );
  },
);

const sizeToDescriptorTextProps: Record<RedoDropdownInputSize, TextSizeValue> =
  { xs: "xs", sm: "xs", md: "sm" };

const sizeClassToCssClass: Record<RedoDropdownInputSize, string> = {
  xs: redoDropdownInputCss.xSmall,
  sm: redoDropdownInputCss.small,
  md: redoDropdownInputCss.regular,
};
